import { FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { DatePicker } from '@/shared/ui';

import { readNoteDate, writeNoteDate } from '../lib';

import styles from './TemplateFieldView.module.scss';

type Props = {
  // YYYY-MM-DD, empty for none.
  value: string;
  // The calendar closed: with the date chosen (empty once emptied) on Enter
  // or leaving it, with undefined on Escape or when the date is unchanged.
  onClose: (date: string | undefined) => void;
};

// The calendar of a date field or cell, where a date is chosen or typed in
// the interface language's format. It takes the focus once it is there,
// which opens it.
export const NoteDatePicker: FC<Props> = ({ value, onClose }) => {
  const { locale } = useIntl();
  // Undefined until the clinician changes the date; null once it is emptied.
  const [draft, setDraft] = useState<Date | null | undefined>(undefined);
  const fieldRef = useRef<HTMLSpanElement>(null);

  // The calendar loads on first use: its input takes the focus once it is
  // there.
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) {
      return undefined;
    }
    const focusInput = () => {
      const input = field.querySelector('input');
      input?.focus();
      return !!input;
    };
    if (focusInput()) {
      return undefined;
    }
    const observer = new MutationObserver(() => {
      if (focusInput()) {
        observer.disconnect();
      }
    });
    observer.observe(field, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const finish = (apply: boolean) => {
    if (!apply || draft === undefined) {
      onClose(undefined);
      return;
    }
    const next = draft ? writeNoteDate(draft) : '';
    onClose(next === value ? undefined : next);
  };

  return (
    <span
      ref={fieldRef}
      className={styles.dateField}
      data-component="workspace-note-date-input"
      onKeyDownCapture={(event) => {
        if (event.key !== 'Enter' && event.key !== 'Escape') {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        finish(event.key === 'Enter');
      }}
      onBlur={(event) => {
        if (!fieldRef.current?.contains(event.relatedTarget as Node)) {
          finish(true);
        }
      }}
    >
      <DatePicker
        className={styles.datePicker}
        locale={locale}
        value={(draft === undefined ? readNoteDate(value) : draft) ?? undefined}
        onChange={setDraft}
      />
    </span>
  );
};
